
import React, { useState, useEffect, useRef } from 'react'
import { IonContent, IonHeader, IonTitle, IonToolbar, IonButtons, IonBackButton, IonCard, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCardContent, IonGrid, IonRow, IonCol, IonSpinner, useIonAlert, IonIcon, IonButton, IonText } from '@ionic/react';
import './Jobs.css';
import { JobItem, JobItemDetail } from '../types/Jobs'
import { sendOriCollectorCommand } from '../data/api';
import moment from 'moment/moment'
import { informationCircleOutline, timeOutline, warningOutline, checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useGlobalUser } from "../GlobalStoreContext";
import { useWebSocketStore } from '../stores/zustang';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Toast } from '@capacitor/toast';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing';
import { useDisplayMode } from "../hooks/useDevice";

type JobItemProps = {
  item: JobItem
}

const JobsDetail = ({ item }: JobItemProps) => {
  const { userId, accessToken, selectedOri } = useGlobalUser();
  const [items, setItems] = useState<JobItemDetail[]>()
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [presentAlert] = useIonAlert();
  const { csvFiles, statuses } = useWebSocketStore();
  const { isMobile } = useDisplayMode();

  // Current device state (START = running)
  const deviceStatus = statuses[selectedOri ?? ''];
  const isRunning = String(deviceStatus?.status_name ?? '').toUpperCase() === 'START';

  const errorDescriptions: Record<0 | 1 | 2, string> = {
    0: 'Okay!',
    1: 'Duplicate tag detected',
    2: 'Empty tag',
  };

  // --- Estados del flujo de escaneo ---
  const [isScanning, setIsScanning] = useState(false);
  const [scanSeconds, setScanSeconds] = useState(10);
  const [scanPhase, setScanPhase] = useState<'idle' | 'waiting' | 'done'>('idle');
  const [scanResult, setScanResult] = useState<null | 'success' | 'no_tag' | 'error'>(null);
  const [scanTag, setScanTag] = useState<string | null>(null);

  const scanningPositionRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const autoCloseTimerRef = useRef<number | null>(null);
  const isScanningRef = useRef<boolean>(false);
  const baselineTagRef = useRef<string>('');
  useEffect(() => { isScanningRef.current = isScanning; }, [isScanning]);

  const requestFilesystemPermission = async () => {
    try {
      await Filesystem.requestPermissions();
    } catch {
      throw new Error("❌ Could not request permissions");
    }
  };

  const safeClear = () => {
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (autoCloseTimerRef.current !== null) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  const stopScanWait = () => {
    setIsScanning(false);
    setScanPhase('idle');
    setScanResult(null);
    setScanTag(null);
    scanningPositionRef.current = null;
    baselineTagRef.current = '';
    safeClear();
  };

  const finishScan = (result: 'success' | 'no_tag' | 'error', tag?: string) => {
    setScanResult(result);
    setScanTag(tag ?? null);
    setScanPhase('done');
    autoCloseTimerRef.current = window.setTimeout(() => {
      stopScanWait();
    }, 1200);
  };

  const getTagForPosition = (pos: number): string => {
    const row = (items ?? []).find(it => Number(it.position) === Number(pos));
    return (row?.tag ?? '').toString().trim();
  };

  const startScanWait = (position: number) => {
    // Snapshot current tag before scanning
    baselineTagRef.current = getTagForPosition(position);

    setScanSeconds(10);
    setIsScanning(true);
    setScanPhase('waiting');
    setScanResult(null);
    setScanTag(null);
    scanningPositionRef.current = position;

    // Countdown only; timeout is handled in useEffect
    countdownTimerRef.current = window.setInterval(() => {
      setScanSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    // Send command (actual data arrives via Zustand → CSV)
    sendOriCollectorCommand(
      userId,
      selectedOri,
      "scan_qr",
      { id_job: item.id_job, position },
      accessToken
    ).catch(() => {
      safeClear();
      finishScan('error');
    });
  };

  // Block scan while job is running (START)
  const handleScanPress = async (position: number) => {
    if (isRunning) {
      await presentAlert({
        header: 'Scan not allowed',
        message: 'You cannot scan while a job is running. Please stop, pause or finish the current job first.',
        buttons: ['OK'],
      });
      return;
    }
    startScanWait(position);
  };

  // ✅ Timeout → no_tag
  useEffect(() => {
    if (!isScanning) return;
    if (scanPhase !== 'waiting') return;
    if (scanSeconds === 0) {
      safeClear();
      finishScan('no_tag');
    }
  }, [scanSeconds, isScanning, scanPhase]);

  // Watch item changes (Zustand → csvFiles → items)
  // If the tag at this position changes vs snapshot and is non-empty → success
  useEffect(() => {
    if (!isScanningRef.current) return;
    if (scanPhase !== 'waiting') return;

    const pos = scanningPositionRef.current;
    if (pos == null) return;

    const currentTag = getTagForPosition(pos);
    const baseline = baselineTagRef.current;

    if (currentTag && currentTag !== baseline) {
      safeClear();
      finishScan('success', currentTag);
    }
  }, [items, scanPhase]);

  // Cleanup on unmount
  useEffect(() => () => stopScanWait(), []);

  const handleDownload = async () => {
    const csvContent = [
      items && items[0] ? Object.keys(items[0]).join(';') : '',
      ...(items ?? []).map(item => Object.values(item).join(';')),
    ].join('\n');
    const fileName = `job_${item.id_job}.csv`;
    try {
      if (Capacitor.getPlatform() === 'web') {
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await requestFilesystemPermission();
        const result = await Filesystem.writeFile({
          path: fileName,
          data: csvContent,
          directory: Directory.External,
          encoding: Encoding.UTF8,
        });
        const fullPath = result.uri;
        await SocialSharing.share('', 'CSV file', fullPath);
      }
    } catch (error) {
      console.error("❌ Error saving file", error);
      await Toast.show({ text: `❌ Could not save the file` });
    }
  };

  const loadItems = async () => {
    sendOriCollectorCommand(userId, selectedOri, "export_csv", { id_job: item.id_job }, accessToken)
      .catch(() => {
        presentAlert({
          header: 'Error',
          message: 'Could not load data.',
          buttons: ['OK'],
        })
      })
  }

  const sendEmailItems = async () => {
    sendOriCollectorCommand(userId, selectedOri, "email_csv", { id_job: item.id_job }, accessToken)
      .catch(() => {
        presentAlert({
          header: 'Error',
          message: 'Could not send the email.',
          buttons: ['OK'],
        })
      })
  }

  const listDateTime = (item: JobItem, listitem: JobItemDetail) => {
    const item_date = moment(item.date_created).format("DD/MM/yyyy")
    const listitem_date = moment(listitem.date_created).format("DD/MM/yyyy")
    let ret;
    if (listitem_date === item_date) {
      ret = moment(listitem.date_created).format("HH:mm:ss")
    } else {
      ret = moment(listitem.date_created).format("DD/MM/yyyy HH:mm:ss")
    }
    return ret
  }

  useEffect(() => {
    setTimeout(() => loadItems(), 1000)
  }, [])

  useEffect(() => {
    const csvEntry = csvFiles[selectedOri ?? ''];
    if (csvEntry && csvEntry.job_id === item.id_job) {
      const parsed = parseCSV(csvEntry.csv);
      setItems(parsed);
      setIsLoaded(true);
    } else {
      setItems(undefined);
      setIsLoaded(false);
    }
  }, [csvFiles, selectedOri, item.id_job]);

  const parseCSV = (csvString: string): JobItemDetail[] => {
    const lines = csvString.trim().split('\n');
    const headers = lines[0].split(';');

    return lines.slice(1).map((line) => {
      const values = line.split(';');
      const entry: any = {};
      headers.forEach((header, index) => {
        entry[header.trim()] = values[index]?.trim();
      });

      return {
        id_job_item: Number(entry.id_job_item),
        id_job: Number(entry.id_job),
        position: Number(entry.position),
        tag: entry.QR_code,
        date_created: entry.date_item,
        errors: Number(entry.errors),
        farm: entry.farm,
        oricollector: entry.oricollector,
        user: entry.user,
        robot: entry.robot,
        job_date_created: entry.job_date_created,
        antenna: 0
      } as JobItemDetail;
    });
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start" >
            <IonBackButton text="Back" ></IonBackButton>
          </IonButtons>
          <IonTitle>Job</IonTitle>
          {items && items.length > 0 && (
            <IonButtons collapse={false} slot="end">
              <IonButton onClick={() => sendEmailItems()}>Email</IonButton>
            </IonButtons>
          )}
          <IonButtons collapse={false} slot="end">
            {items && items.length > 0 && (
              <IonButton onClick={handleDownload}>
                Download
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Job</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{item.farm}</IonCardTitle>
            <IonCardSubtitle>Job details for ID: {item.id_job}</IonCardSubtitle>
            <IonCardSubtitle>{moment(item.date_created).format("DD/MM/yyyy HH:mm:ss")}</IonCardSubtitle>
          </IonCardHeader>

          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol>
                  Ori Collector<br />
                  <strong>{item.oricollector}</strong>
                </IonCol>
                <IonCol>
                  Robot<br />
                  <strong>{item.robot}</strong>
                </IonCol>
                <IonCol>
                  User<br />
                  <strong>{item.user}</strong>
                </IonCol>
                <IonCol>
                  Samples<br />
                  <strong>{item.sample_tray}</strong>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {isLoaded ? (
          <>
            {items && items.length > 0 ? (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Data</IonCardTitle>
                </IonCardHeader>
                {isMobile ? (
                  <IonCardContent>
                    <IonGrid className="grid-stripped">
                      <IonRow className="grid-header" style={{ position: 'sticky', top: '0', left: '0' }}>
                        <IonCol>Position</IonCol>
                        <IonCol>Tag</IonCol>
                        <IonCol>Date</IonCol>
                        <IonCol>Scan</IonCol>
                        <IonCol>Status</IonCol>
                      </IonRow>

                      {items?.map((listitem: JobItemDetail) => (
                        <IonRow key={listitem.id_job_item}>
                          <IonCol>{listitem.position}</IonCol>

                          <IonCol style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {listitem.tag ? (
                              <Tippy content={listitem.tag}>
                                <IonIcon
                                  icon={informationCircleOutline}
                                  style={{ color: '#555', fontSize: '20px', cursor: 'pointer' }}
                                />
                              </Tippy>
                            ) : (
                              <span style={{ color: '#aaa', fontSize: '14px' }}>–</span>
                            )}
                          </IonCol>

                          <IonCol style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Tippy content={listDateTime(item, listitem)}>
                              <IonIcon
                                icon={timeOutline}
                                style={{ color: '#555', fontSize: '20px', cursor: 'pointer' }}
                              />
                            </Tippy>
                          </IonCol>

                          <IonCol>
                            <IonButton
                              size="small"
                              onClick={() => handleScanPress(listitem.position)}
                              disabled={isScanning}
                              color={isRunning ? 'medium' : undefined}
                            >
                              Scan
                            </IonButton>
                          </IonCol>

                          <IonCol style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Tippy content={errorDescriptions[listitem.errors as 0 | 1 | 2]}>
                              {listitem.errors === 0 ? (
                                <IonIcon icon={checkmarkCircleOutline} style={{ color: 'green', fontSize: '20px', cursor: 'pointer' }} />
                              ) : (
                                <IonIcon icon={warningOutline} style={{ color: 'red', fontSize: '20px', cursor: 'pointer' }} />
                              )}
                            </Tippy>
                          </IonCol>
                        </IonRow>
                      ))}
                    </IonGrid>
                  </IonCardContent>
                ) : (
                  <IonCardContent>
                    <IonGrid className="grid-stripped">
                      <IonRow className="grid-header" style={{ position: 'sticky', top: '0', left: '0' }}>
                        <IonCol>Position</IonCol>
                        <IonCol>Tag</IonCol>
                        <IonCol>Date</IonCol>
                        <IonCol>Scan</IonCol>
                        <IonCol>Status</IonCol>
                      </IonRow>
                      {items?.map((listitem: JobItemDetail) => (
                        <IonRow key={listitem.id_job_item}>
                          <IonCol>{listitem.position}</IonCol>
                          <IonCol>{listitem.tag}</IonCol>
                          <IonCol>{listDateTime(item, listitem)}</IonCol>
                          <IonCol>
                            <IonButton
                              onClick={() => handleScanPress(listitem.position)}
                              disabled={isScanning}
                              color={isRunning ? 'medium' : undefined}
                            >
                              Scan
                            </IonButton>
                          </IonCol>
                          <IonCol style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Tippy content={errorDescriptions[listitem.errors as 0 | 1 | 2]}>
                              {listitem.errors === 0 ? (
                                <IonIcon icon={checkmarkCircleOutline} style={{ color: 'green', fontSize: '24px', cursor: 'pointer' }} />
                              ) : (
                                <IonIcon icon={warningOutline} style={{ color: 'red', fontSize: '24px', cursor: 'pointer' }} />
                              )}
                            </Tippy>
                          </IonCol>
                        </IonRow>
                      ))}
                    </IonGrid>
                  </IonCardContent>
                )}
              </IonCard>
            ) : (
              <p className="ion-text-center ion-justify-content-center">No data.</p>
            )}
          </>
        ) : (
          <IonGrid>
            <IonRow>
              <IonCol className="ion-text-center">
                <IonSpinner></IonSpinner>
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
      </IonContent>

      {/* ===== Blocking overlay ===== */}
      {isScanning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: 'min(92vw, 460px)',
              borderRadius: 16,
              background: 'var(--ion-background-color, #fff)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
              padding: 24,
              textAlign: 'center',
              color: 'var(--ion-text-color, #000)',
            }}
          >
            {scanPhase === 'waiting' && (
              <>
                <IonSpinner />
                <h2 style={{ fontSize: 20, marginTop: 8, marginBottom: 6 }}>Scanning…</h2>
                <p style={{ margin: 0 }}>Waiting for OriCollector response</p>
                <p style={{ marginTop: 6 }}><strong>{scanSeconds}s</strong> remaining</p>
                <IonIcon icon={timeOutline} style={{ fontSize: 22, opacity: 0.55 }} />
              </>
            )}

            {scanPhase === 'done' && (
              <>
                {scanResult === 'success' && (
                  <>
                    <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 60 }} />
                    <h2 style={{ fontSize: 20, marginTop: 10 }}>Scan completed</h2>
                    {scanTag && <p>Tag: <strong>{scanTag}</strong></p>}
                  </>
                )}
                {scanResult === 'no_tag' && (
                  <>
                    <IonIcon icon={closeCircleOutline} style={{ fontSize: 60 }} />
                    <h2 style={{ fontSize: 20, marginTop: 10 }}>No New Tag Detected</h2>
                    <p style={{ opacity: .7 }}>Closing…</p>
                  </>
                )}
                {scanResult === 'error' && (
                  <>
                    <IonIcon icon={closeCircleOutline} style={{ fontSize: 60 }} />
                    <h2 style={{ fontSize: 20, marginTop: 10 }}>Error starting scan</h2>
                    <p style={{ opacity: .7 }}>Please try again.</p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* ===== End overlay ===== */}
    </>
  );
};

export default JobsDetail;
